import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import i18n from '@/i18n'
import {
  useTemplate,
  useCreateTemplate,
  useUpdateTemplate,
  useUploadTemplateThumbnail,
  useAddTemplateReferences,
  useDeleteReference,
  useStyles,
  useCategories,
  apiClient,
} from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { I18nFieldsSection, I18nPromptFieldsSection } from '../components/I18nFieldsSection'
import { X, Upload, ImagePlus } from 'lucide-react'
import { toast } from 'sonner'
import { ASPECT_RATIO_GROUPS } from '@/lib/constants'

// 어드민 UI는 한국어 고정 - 비율 라벨도 ko 리소스에서 직접 조회한다
const tKo = i18n.getFixedT('ko')

export function TemplateForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEdit = !!id

  const { data: template } = useTemplate(id!)
  const { data: styles } = useStyles()
  const { data: categories } = useCategories()
  const createMutation = useCreateTemplate()
  const updateMutation = useUpdateTemplate(id!)
  const uploadThumbnailMutation = useUploadTemplateThumbnail(id!)
  const addReferencesMutation = useAddTemplateReferences()
  const deleteReferenceMutation = useDeleteReference()

  // 생성 모드에서 임시 보관할 레퍼런스 이미지 (pending upload)
  const [pendingReferences, setPendingReferences] = useState<
    Array<{
      id: string
      file: File
      preview: string // Data URL
    }>
  >([])

  type TemplateFormData = {
    categoryId: string
    name: string
    description: string
    nameI18n: Record<string, string>
    descriptionI18n: Record<string, string>
    textPrompt: string
    textPromptI18n: Record<string, string>
    styleId: string
    promptSuffix: string
    modelId: string
    aspectRatio: string
    listOrder: number
    enabled: boolean
  }

  const baseFormData = useMemo<TemplateFormData>(
    () =>
      template
        ? {
            categoryId: template.categoryId || '',
            name: template.name,
            description: template.description || '',
            nameI18n: template.nameI18n ?? {},
            descriptionI18n: template.descriptionI18n ?? {},
            textPrompt: template.textPrompt,
            textPromptI18n: template.textPromptI18n ?? {},
            styleId: template.styleId || '',
            promptSuffix: template.promptSuffix || '',
            modelId: template.modelId || '',
            aspectRatio: template.aspectRatio,
            listOrder: template.listOrder,
            enabled: template.enabled,
          }
        : {
            categoryId: '',
            name: '',
            description: '',
            nameI18n: {},
            descriptionI18n: {},
            textPrompt: '',
            textPromptI18n: {},
            styleId: '',
            promptSuffix: '',
            modelId: '',
            aspectRatio: '1:1',
            listOrder: 0,
            enabled: true,
          },
    [template],
  )
  const [draftFormData, setDraftFormData] = useState<TemplateFormData | null>(null)
  const formData = draftFormData ?? baseFormData

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const [thumbnailPreviewOverride, setThumbnailPreviewOverride] = useState<string | null>(null)
  const baseThumbnailPreview =
    template?.thumbnailUrl ? `${template.thumbnailUrl}?v=${new Date(template.updatedAt).getTime()}` : ''
  const thumbnailPreview = thumbnailPreviewOverride ?? baseThumbnailPreview

  const updateField = <K extends keyof TemplateFormData>(key: K, value: TemplateFormData[K]) => {
    setDraftFormData((prev) => ({ ...(prev ?? baseFormData), [key]: value }))
  }

  const updateI18nField = (field: 'nameI18n' | 'descriptionI18n', lang: string, text: string) => {
    setDraftFormData((prev) => {
      const base = prev ?? baseFormData
      return { ...base, [field]: { ...base[field], [lang]: text } }
    })
  }

  const updateTextPromptI18n = (lang: string, text: string) => {
    setDraftFormData((prev) => {
      const base = prev ?? baseFormData
      return { ...base, textPromptI18n: { ...base.textPromptI18n, [lang]: text } }
    })
  }

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setThumbnailFile(file)
    const reader = new FileReader()
    reader.onload = () => setThumbnailPreviewOverride(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleReferenceImagesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    // 수정 모드: 기존처럼 즉시 API 호출
    if (isEdit && id) {
      const currentCount = template?.references?.length || 0

      if (currentCount + files.length > 4) {
        toast.error('레퍼런스 이미지는 최대 4개까지 추가할 수 있습니다')
        e.target.value = ''
        return
      }

      try {
        const images = await Promise.all(
          files.map((file, index) => {
            return new Promise<{ dataUrl: string; listOrder: number }>((resolve) => {
              const reader = new FileReader()
              reader.onload = () => {
                resolve({
                  dataUrl: reader.result as string,
                  listOrder: currentCount + index,
                })
              }
              reader.readAsDataURL(file)
            })
          })
        )

        await addReferencesMutation.mutateAsync({ templateId: id, images })
        toast.success('레퍼런스 이미지가 추가되었습니다')
      } catch (error: unknown) {
        toast.error(`이미지 업로드 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`)
      }

      e.target.value = ''
      return
    }

    // 생성 모드: state에 임시 보관
    const currentCount = pendingReferences.length

    if (currentCount + files.length > 4) {
      toast.error('레퍼런스 이미지는 최대 4개까지 추가할 수 있습니다')
      e.target.value = ''
      return
    }

    const newRefs = await Promise.all(
      files.map(async (file) => {
        const preview = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(file)
        })
        return {
          id: `pending-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          file,
          preview,
        }
      })
    )

    setPendingReferences((prev) => [...prev, ...newRefs])
    e.target.value = ''
  }

  const handleDeleteReference = async (refId: string) => {
    if (!confirm('이 레퍼런스 이미지를 삭제하시겠습니까?')) return

    try {
      await deleteReferenceMutation.mutateAsync(refId)
      toast.success('레퍼런스 이미지가 삭제되었습니다')
    } catch (error: unknown) {
      toast.error(`삭제 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`)
    }
  }

  // 생성 모드에서 pending 레퍼런스 삭제
  const handleDeletePendingReference = (pendingId: string) => {
    setPendingReferences((prev) => prev.filter((ref) => ref.id !== pendingId))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      let templateId = id

      // 1. 템플릿 생성 또는 수정
      if (isEdit) {
        await updateMutation.mutateAsync(formData)
        toast.success('템플릿이 수정되었습니다')
      } else {
        const created = await createMutation.mutateAsync(formData)
        templateId = created.id
        toast.success('템플릿이 생성되었습니다')
      }

      // 2. 썸네일 업로드 (있는 경우)
      // 생성 모드에서는 새로 생성된 templateId 사용을 위해 mutationFn 직접 호출
      if (thumbnailFile && templateId) {
        const dataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(thumbnailFile)
        })

        if (isEdit) {
          await uploadThumbnailMutation.mutateAsync(dataUrl)
        } else {
          // 생성 모드: 직접 API 호출 (훅은 id가 없어서 사용 불가)
          await apiClient.post(`/admin/templates/${templateId}/thumbnail`, { dataUrl })
        }
        toast.success('썸네일이 업로드되었습니다')
      }

      // 3. 레퍼런스 이미지 업로드 (생성 모드에서만)
      if (!isEdit && pendingReferences.length > 0 && templateId) {
        const images = await Promise.all(
          pendingReferences.map(async (ref, index) => ({
            dataUrl: ref.preview,
            listOrder: index,
          }))
        )
        await addReferencesMutation.mutateAsync({ templateId, images })
        toast.success('레퍼런스 이미지가 업로드되었습니다')
      }

      // 생성 모드: 썸네일/레퍼런스 업로드가 raw fetch로 처리되어 캐시 무효화 누락됨
      if (!isEdit) {
        await queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
      }

      // 수정 시 상세 페이지로, 생성 시 목록으로 이동
      if (isEdit) {
        navigate(`/admin/templates/${id}/view`)
      } else {
        navigate('/admin/templates')
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '저장 중 오류가 발생했습니다')
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-heading tracking-tight">
          {isEdit ? '템플릿 수정' : '새 템플릿'}
        </h1>
        <p className="text-caption mt-2 leading-relaxed">
          {isEdit ? '템플릿 정보를 수정합니다' : '새로운 템플릿을 생성합니다'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg border">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">이름 *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
            placeholder="템플릿 이름"
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">설명</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="템플릿 설명"
            rows={2}
          />
        </div>

        {/* Translations */}
        <I18nFieldsSection
          value={{ nameI18n: formData.nameI18n, descriptionI18n: formData.descriptionI18n }}
          onChange={updateI18nField}
          withDescription
        />

        {/* Text Prompt */}
        <div className="space-y-2">
          <Label htmlFor="textPrompt">프롬프트 *</Label>
          <Textarea
            id="textPrompt"
            value={formData.textPrompt}
            onChange={(e) => updateField('textPrompt', e.target.value)}
            required
            placeholder="이미지 생성 프롬프트"
            rows={4}
          />
        </div>

        {/* Prompt Translations */}
        <I18nPromptFieldsSection value={formData.textPromptI18n} onChange={updateTextPromptI18n} />

        {/* Category */}
        <div className="space-y-2">
          <Label htmlFor="categoryId">카테고리</Label>
          <select
            id="categoryId"
            value={formData.categoryId}
            onChange={(e) => updateField('categoryId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">선택 안함</option>
            {categories?.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* Style */}
        <div className="space-y-2">
          <Label htmlFor="styleId">스타일</Label>
          <select
            id="styleId"
            value={formData.styleId}
            onChange={(e) => updateField('styleId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">선택 안함</option>
            {styles?.map((style) => (
              <option key={style.id} value={style.id}>
                {style.name}
              </option>
            ))}
          </select>
        </div>

        {/* Prompt Suffix */}
        <div className="space-y-2">
          <Label htmlFor="promptSuffix">프롬프트 접미사</Label>
          <Textarea
            id="promptSuffix"
            value={formData.promptSuffix}
            onChange={(e) => updateField('promptSuffix', e.target.value)}
            placeholder="스타일의 promptSuffix 대신 사용할 커스텀 접미사"
            rows={2}
          />
          <p className="text-sm text-gray-500">
            입력 시 스타일의 프롬프트 접미사 대신 이 값이 적용됩니다.
          </p>
        </div>

        {/* Aspect Ratio */}
        <div className="space-y-2">
          <Label htmlFor="aspectRatio">비율</Label>
          <select
            id="aspectRatio"
            value={formData.aspectRatio}
            onChange={(e) => updateField('aspectRatio', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            {ASPECT_RATIO_GROUPS.map((group) => (
              <optgroup key={group.labelKey} label={tKo(group.labelKey)}>
                {group.ratios.map((ratio) => (
                  <option key={ratio.id} value={ratio.id}>
                    {ratio.id} ({tKo(ratio.labelKey)})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* List Order */}
        <div className="space-y-2">
          <Label htmlFor="listOrder">정렬 순서</Label>
          <Input
            id="listOrder"
            type="number"
            value={formData.listOrder}
            onChange={(e) => updateField('listOrder', Number.parseInt(e.target.value, 10) || 0)}
          />
        </div>

        {/* Enabled */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="enabled"
            checked={formData.enabled}
            onChange={(e) => updateField('enabled', e.target.checked)}
            className="w-4 h-4 rounded border-gray-300"
          />
          <Label htmlFor="enabled">활성화</Label>
        </div>

        {/* Thumbnail */}
        <div className="space-y-2">
          <Label>썸네일</Label>
          <div
            className={`relative w-48 h-48 rounded-lg overflow-hidden ${
              thumbnailPreview
                ? 'group'
                : 'border-2 border-dashed border-slate-300 hover:border-primary/50 bg-slate-50 hover:bg-slate-100 cursor-pointer'
            }`}
            onClick={!thumbnailPreview ? () => document.getElementById('thumbnail')?.click() : undefined}
          >
            {thumbnailPreview ? (
              <>
                <img
                  src={thumbnailPreview}
                  alt="썸네일 미리보기"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        document.getElementById('thumbnail')?.click()
                      }}
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                      title="썸네일 변경"
                    >
                      <ImagePlus className="w-4 h-4 text-slate-700" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setThumbnailFile(null)
                        setThumbnailPreviewOverride('')
                      }}
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                      title="썸네일 삭제"
                    >
                      <X className="w-4 h-4 text-slate-700" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                <ImagePlus className="w-8 h-8 text-slate-400" />
                <span className="text-sm text-slate-500">썸네일 업로드</span>
              </div>
            )}
          </div>
          <input
            type="file"
            id="thumbnail"
            accept="image/*"
            onChange={handleThumbnailChange}
            className="hidden"
          />
        </div>

        {/* Reference Images */}
        <div className="space-y-4 pt-6 border-t">
          <div className="flex items-center justify-between">
            <div>
              <Label>레퍼런스 이미지</Label>
              <p className="text-sm text-gray-600 mt-1">
                최대 4개까지 추가 가능 (현재:{' '}
                {isEdit ? template?.references?.length || 0 : pendingReferences.length}개)
              </p>
            </div>
            {(isEdit ? (template?.references?.length || 0) : pendingReferences.length) < 4 && (
              <label htmlFor="referenceImages">
                <Button type="button" variant="outline" size="sm" asChild>
                  <span className="cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" />
                    이미지 추가
                  </span>
                </Button>
                <input
                  type="file"
                  id="referenceImages"
                  accept="image/*"
                  multiple
                  onChange={handleReferenceImagesChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* 수정 모드: 서버에 저장된 레퍼런스 이미지 표시 */}
          {isEdit && template?.references && template.references.length > 0 && (
            <div className="grid grid-cols-4 gap-4">
              {template.references.map((ref) => (
                <div key={ref.id} className="relative group bg-gray-50 rounded-lg border">
                  <img
                    src={`${ref.thumbnailUrl}?v=${new Date(ref.createdAt).getTime()}`}
                    alt="Reference"
                    className="w-full h-32 object-contain rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteReference(ref.id)}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* 생성 모드: 업로드 대기 중인 레퍼런스 이미지 표시 */}
          {!isEdit && pendingReferences.length > 0 && (
            <div className="grid grid-cols-4 gap-4">
              {pendingReferences.map((ref) => (
                <div key={ref.id} className="relative group bg-gray-50 rounded-lg border">
                  <img
                    src={ref.preview}
                    alt="Reference (pending)"
                    className="w-full h-32 object-contain rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => handleDeletePendingReference(ref.id)}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex items-center space-x-4 pt-4">
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {isEdit ? '수정' : '생성'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            취소
          </Button>
        </div>
      </form>
    </div>
  )
}
